import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { toast } from "sonner";
import {
  postPtzMove,
  postPtzStop,
  postPtzStopKeepalive,
  postZoom,
  postZoomStopKeepalive,
  type PtzDirection,
} from "@client/api/foscam";
import { t } from "@client/lib/dictionary";
import { foscamErrMsg } from "@client/lib/foscam/apiError";
import {
  MAX_HOLD_MS,
  STOP_RETRY_DELAYS_MS,
  holdLabel,
  holdTone,
  initialHoldState,
  reduceHold,
  ringProgress,
  type HoldState,
  type HoldTone,
  type PtzAxis,
} from "@client/lib/foscam/ptzHold";

/**
 * „Drž pro pohyb" u PTZ kamery — napojení čistého automatu (`lib/foscam/ptzHold.ts`)
 * na REST API, časovače, haptiku a únikové cesty.
 *
 * Tři věci, které tenhle hook řeší a původní inline implementace v CameraControls ne:
 *
 *  1. **Okamžitá odezva.** `press` se dispatchuje PŘED odesláním requestu, takže
 *     prstenec se plní od nulté milisekundy. Při ~6 s zpoždění obrazu je to jediná
 *     zpětná vazba, kterou uživatel v prvních sekundách má.
 *  2. **Garantovaný stop.** Původní kód měl `postPtzStop(slug).catch(() => undefined)` —
 *     když stop selhal, kamera se točila dál a nikdo se to nedozvěděl. Tady se stop
 *     opakuje s backoffem, posílá se i na pointercancel / skrytou záložku / unmount /
 *     zavření stránky (keepalive fetch) a po vyčerpání pokusů zůstane trvalý alarm.
 *  3. **Bezpečnostní strop.** Zaseknutý `pointerup` (typicky přepnutá aplikace na
 *     Androidu) už kameru neotáčí donekonečna.
 *
 * Gating requestů jede přes `activeAxis` ref, ne přes stav z `useReducer` — ref je
 * aktuální synchronně, takže dva stisky ve stejném ticku nepošlou dva `ptzMove`.
 */

/**
 * Jak dlouho se u DOTYKOVÉHO vstupu čeká, než se pohyb opravdu odešle.
 *
 * Ovládání je nově trvale viditelné přes spodní hranu dlaždice — tedy přesně tam,
 * kde palec začíná swipe na další kameru ve fullscreenu wrapperu. Bez téhle prodlevy
 * by každé takové posunutí kamerou cuklo. Prstenec se plní od nulté ms, takže se
 * odezva UI nezpomalí; odkládá se jen síťový příkaz.
 */
const TOUCH_INTENT_MS = 120;

/**
 * Délka jednoho drcnutí (`nudge`) — používá edge-push digitálního zoomu.
 *
 * 400 ms je nad mrtvou zónou pan motoru (naměřeno ~290 ms při rychlosti 2, viz
 * docs/plans/KAMERY-ZOOM-A-MIRENI.md §1.4). Kratší pulz by u vodorovné osy nemusel
 * kamerou hnout vůbec.
 */
const NUDGE_PULSE_MS = 400;

const isZoomAxis = (a: PtzAxis): a is "zoomIn" | "zoomOut" => a === "zoomIn" || a === "zoomOut";

const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

const vibrate = (pattern: number | number[]): void => {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* nepodporováno / blokováno — nic se neděje */
  }
};

export interface PtzHoldApi {
  state: HoldState;
  /** 0..1 — plnění během držení, odpočet během dojezdu obrazu. */
  progress: number;
  label: string | null;
  tone: HoldTone;
  /** Něco se děje → ovládání se nesmí schovat auto-hidem. */
  busy: boolean;
  /** Pointer/klávesové handlery na jedno směrové nebo zoom tlačítko. */
  holdProps: (axis: PtzAxis) => React.DOMAttributes<HTMLElement>;
  /** Ruční Stop (tlačítko uprostřed kříže) — funguje i mimo držení. */
  stopNow: () => void;
  /** Jedno krátké drcnutí bez držení (edge-push digitálního zoomu). */
  nudge: (axis: PtzAxis) => void;
}

export function usePtzHold(slug: string, latencyMs: number): PtzHoldApi {
  const [state, dispatch] = useReducer(reduceHold, initialHoldState);
  const [now, setNow] = useState(() => Date.now());

  /** Osa, která právě jede. Synchronní zdroj pravdy pro gating requestů. */
  const activeAxis = useRef<PtzAxis | null>(null);
  /** Roste s každým stopem — zneplatní čekající retry předchozího pohybu. */
  const stopToken = useRef(0);
  const capTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Timer, který ukončí drcnutí. Musí se uklidit, jinak by pustil stop po unmountu. */
  const nudgeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Odložený `ptzMove` (intent delay u dotyku) — dokud běží, kamera nedostala nic. */
  const pendingMove = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Odešel v tomhle stisku pohyb? Rozhoduje, jestli je vůbec co zastavovat. */
  const moveSent = useRef(false);

  // Aktuální hodnoty pro asynchronní callbacky. Zápis v efektu (ne v renderu) —
  // efekty se propíšou po commitu, tedy dřív než jakákoli uživatelská interakce.
  const latencyRef = useRef(latencyMs);
  const slugRef = useRef(slug);
  useEffect(() => {
    latencyRef.current = latencyMs;
    slugRef.current = slug;
  }, [latencyMs, slug]);

  const clearCap = useCallback(() => {
    if (capTimer.current) {
      clearTimeout(capTimer.current);
      capTimer.current = null;
    }
  }, []);

  const clearPending = useCallback(() => {
    if (pendingMove.current) {
      clearTimeout(pendingMove.current);
      pendingMove.current = null;
    }
  }, []);

  /**
   * Odešle stop a opakuje ho podle STOP_RETRY_DELAYS_MS. Po vyčerpání pokusů
   * zůstane trvalý toast — kamera možná pořád jede a uživatel to musí vědět.
   */
  const sendStop = useCallback(async (axis: PtzAxis) => {
    const token = ++stopToken.current;
    const cam = slugRef.current;
    const attempts = STOP_RETRY_DELAYS_MS.length + 1;

    for (let i = 0; i < attempts; i++) {
      if (token !== stopToken.current) return; // mezitím nový stisk — ten si stop řeší sám
      try {
        await (isZoomAxis(axis) ? postZoom(cam, "stop") : postPtzStop(cam));
        if (token === stopToken.current) {
          dispatch({ t: "stopOk", now: Date.now(), latencyMs: latencyRef.current });
        }
        return;
      } catch (err) {
        dispatch({ t: "stopFail", now: Date.now(), message: foscamErrMsg(err) });
        const delay = STOP_RETRY_DELAYS_MS[i];
        if (delay === undefined) break;
        await sleep(delay);
      }
    }
    if (token === stopToken.current) {
      toast.error(t.foscam.stopFailed, { duration: Infinity, id: `ptz-stop-${cam}` });
    }
  }, []);

  /** Odešle pohyb a ohlásí výsledek automatu. */
  const sendMove = useCallback(
    (axis: PtzAxis): Promise<void> => {
      moveSent.current = true;
      const cam = slugRef.current;
      const req = isZoomAxis(axis)
        ? postZoom(cam, axis === "zoomIn" ? "in" : "out")
        : postPtzMove(cam, axis as PtzDirection);

      return req
        .then(() => {
          dispatch({ t: "startOk", now: Date.now() });
        })
        .catch((err: unknown) => {
          clearCap();
          if (activeAxis.current === axis) activeAxis.current = null;
          const message = foscamErrMsg(err);
          dispatch({ t: "startFail", now: Date.now(), message });
          toast.error(message);
        });
    },
    [clearCap]
  );

  const stopAxis = useCallback(
    (axis: PtzAxis, reason: "release" | "cap" | "cancel") => {
      clearCap();
      clearPending();
      activeAxis.current = null;

      if (!moveSent.current) {
        // Pohyb se ještě nestihl odeslat (intent delay u dotyku).
        if (reason !== "release") {
          // Gesto přešlo ve scroll / okno ztratilo fokus → kamera se nehnula,
          // takže není co zastavovat a nemá smysl posílat nic.
          dispatch({ t: "reset" });
          return;
        }
        // Vědomý krátký tap = drcnutí. Pohyb se pošle teď a stop hned za ním,
        // ať tapnutí na šipku pořád kamerou trochu pohne.
        dispatch({ t: "release", now: Date.now() });
        void sendMove(axis).finally(() => void sendStop(axis));
        return;
      }

      dispatch(reason === "cap" ? { t: "safetyCap", now: Date.now() } : { t: "release", now: Date.now() });
      void sendStop(axis);
    },
    [clearCap, clearPending, sendMove, sendStop]
  );

  const press = useCallback(
    (axis: PtzAxis, intentDelayMs = 0) => {
      // Jedna osa v jednu chvíli — `ptzStopRun` je globální, překryv dvou směrů by
      // znamenal, že stop jednoho zastaví pohyb druhého.
      if (activeAxis.current) return;
      activeAxis.current = axis;
      moveSent.current = false;

      // Nový pohyb ruší případný trvalý alarm z předchozího selhaného stopu.
      toast.dismiss(`ptz-stop-${slugRef.current}`);
      dispatch({ t: "press", axis, now: Date.now() });
      vibrate(15);

      clearCap();
      capTimer.current = setTimeout(() => {
        capTimer.current = null;
        if (activeAxis.current === axis) {
          vibrate([10, 30, 10]);
          stopAxis(axis, "cap");
        }
      }, MAX_HOLD_MS);

      clearPending();
      if (intentDelayMs > 0) {
        // Prstenec se plní od nulté ms, ale síťový příkaz počká — když se gesto
        // do té doby ukáže jako scroll, kamera se vůbec nehne.
        pendingMove.current = setTimeout(() => {
          pendingMove.current = null;
          if (activeAxis.current === axis) void sendMove(axis);
        }, intentDelayMs);
      } else {
        void sendMove(axis);
      }
    },
    [clearCap, clearPending, sendMove, stopAxis]
  );

  const release = useCallback(
    (axis: PtzAxis) => {
      if (activeAxis.current !== axis) return;
      stopAxis(axis, "release");
    },
    [stopAxis]
  );

  /** Prohlížeč gesto převzal (scroll) — na rozdíl od puštění se nic neodesílá. */
  const cancel = useCallback(
    (axis: PtzAxis) => {
      if (activeAxis.current !== axis) return;
      stopAxis(axis, "cancel");
    },
    [stopAxis]
  );

  /**
   * Pointer capture zajistí, že `pointerup` dorazí i po vyjetí mimo tlačítko,
   * a že pouhý hover (bez stisku) nic neposílá.
   */
  const holdProps = useCallback(
    (axis: PtzAxis): React.DOMAttributes<HTMLElement> => ({
      onPointerDown: (e: React.PointerEvent) => {
        e.preventDefault();
        (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
        // U dotyku se příkaz odloží: prst na tlačítku může být začátek scrollu
        // seznamu kamer, ne úmysl otočit kamerou. Myš/pero odesílá hned.
        press(axis, e.pointerType === "touch" ? TOUCH_INTENT_MS : 0);
      },
      onPointerUp: (e: React.PointerEvent) => {
        const el = e.currentTarget as HTMLElement;
        if (el.hasPointerCapture?.(e.pointerId)) el.releasePointerCapture(e.pointerId);
        release(axis);
      },
      // Prohlížeč gesto převzal (scroll) — na rozdíl od puštění se nic neodesílá.
      onPointerCancel: () => cancel(axis),
      onLostPointerCapture: () => cancel(axis),
      // Klávesnice: držení Enter/mezerníku = držení tlačítka. `repeat` guard, ať
      // autorepeat neposílá move dvacetkrát za sekundu.
      onKeyDown: (e: React.KeyboardEvent) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        if (e.repeat) return;
        e.preventDefault();
        press(axis);
      },
      onKeyUp: (e: React.KeyboardEvent) => {
        if (e.key !== "Enter" && e.key !== " ") return;
        e.preventDefault();
        release(axis);
      },
    }),
    [press, release, cancel]
  );

  const stopNow = useCallback(() => {
    const axis = activeAxis.current;
    clearCap();
    if (axis) {
      stopAxis(axis, "release");
      return;
    }
    // Mimo držení: pošli stop naprázdno (uživatel řeší „kamera se pořád točí")
    // a zruš případný alarm.
    const cam = slugRef.current;
    toast.dismiss(`ptz-stop-${cam}`);
    stopToken.current++;
    dispatch({ t: "reset" });
    void postPtzStop(cam).catch(() => undefined);
    void postZoom(cam, "stop").catch(() => undefined);
  }, [clearCap, stopAxis]);

  /**
   * Krátké drcnutí kamerou — jeden pulz bez držení.
   *
   * Jede schválně přes `press`/`release`, ne přes vlastní `postPtzMove`: tím zdarma
   * zdědí gating na jednu osu, bezpečnostní strop, opakování stopu i keepalive při
   * zavření stránky. Vlastní cesta by to všechno musela zopakovat — a přesně tomu se
   * tenhle hook od začátku vyhýbá.
   */
  const nudge = useCallback(
    (axis: PtzAxis) => {
      if (activeAxis.current) return; // něco už jede, druhý směr by si přebil stop
      press(axis, 0);
      if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
      nudgeTimer.current = setTimeout(() => {
        nudgeTimer.current = null;
        release(axis);
      }, NUDGE_PULSE_MS);
    },
    [press, release]
  );

  // Animační smyčka jen když se něco děje. rAF (ne setInterval) kvůli plynulosti
  // prstence; při `idle` se smyčka vypne a nic nežere.
  const busy = state.phase !== "idle";
  useEffect(() => {
    if (!busy) return;
    let raf = 0;
    const loop = () => {
      const n = Date.now();
      setNow(n);
      dispatch({ t: "tick", now: n });
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [busy]);

  // Únikové cesty. Skrytá záložka i ztráta fokusu okna znamenají, že `pointerup`
  // už nemusí dorazit — kamera by jela dál.
  useEffect(() => {
    // "cancel", ne "release": když se pohyb ještě nestihl odeslat, nemá smysl ho
    // teď dohánět drcnutím — okno už není v popředí.
    const bail = () => {
      const axis = activeAxis.current;
      if (axis) stopAxis(axis, "cancel");
    };
    const onVisibility = () => {
      if (document.hidden) bail();
    };
    /** Zavření stránky — axios request by se zrušil, proto keepalive fetch. */
    const onPageHide = () => {
      const axis = activeAxis.current;
      if (!axis) return;
      if (isZoomAxis(axis)) postZoomStopKeepalive(slugRef.current);
      else postPtzStopKeepalive(slugRef.current);
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", bail);
    window.addEventListener("pagehide", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", bail);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [stopAxis]);

  // Unmount během držení (zavření spotlightu, sbalení wrapperu) — stop se musí
  // odeslat i tak. Fire-and-forget: komponenta už neexistuje, ale kamera ano.
  useEffect(
    () => () => {
      clearCap();
      clearPending();
      if (nudgeTimer.current) clearTimeout(nudgeTimer.current);
      const axis = activeAxis.current;
      activeAxis.current = null;
      // Když se pohyb nestihl odeslat, kamera nic nedostala → není co zastavovat.
      if (!axis || !moveSent.current) return;
      stopToken.current++;
      const cam = slugRef.current;
      void (isZoomAxis(axis) ? postZoom(cam, "stop") : postPtzStop(cam)).catch(() => undefined);
    },
    [clearCap, clearPending]
  );

  return {
    state,
    progress: ringProgress(state, now),
    label: holdLabel(state, now),
    tone: holdTone(state),
    busy,
    holdProps,
    stopNow,
    nudge,
  };
}
